/* Spec PARTNER-001 — formatacao de datas e idade. Funcoes puras.
   Spec PARTNER-005 D-04 / FR-003: o idioma padrao e o da interface (o i18n chama setFormatLocale a cada troca) e os
   horarios sempre no fuso de Brasilia; datas sem hora ('AAAA-MM-DD') saem como estao, sem deslocamento de fuso. */

export const DEFAULT_LOCALE = 'pt-BR'
export const TIME_ZONE = 'America/Sao_Paulo'

let currentLocale = DEFAULT_LOCALE

/** Idioma usado quando a chamada nao passa um (o da interface). */
export function setFormatLocale(locale: string) {
    currentLocale = locale
}

export function formatLocale(): string {
    return currentLocale
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

/* 'AAAA-MM-DD' → meia-noite UTC, formatada em UTC (o dia nao muda com o fuso do aparelho). */
function parseIsoDate(iso: string): Date {
    const [y, m, d] = iso.split('-').map(Number)
    return new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1))
}

function dateParts(iso: string): { date: Date; timeZone: string } {
    return DATE_ONLY.test(iso) ? { date: parseIsoDate(iso), timeZone: 'UTC' } : { date: new Date(iso), timeZone: TIME_ZONE }
}

/* "06/10/2026" (pt-BR), "10/06/2026" (en-US), "2026/10/06" (ja-JP) */
export function formatDate(iso: string, locale = currentLocale): string {
    const { date, timeZone } = dateParts(iso)
    return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone }).format(date)
}

/* "6 de outubro de 2026" */
export function formatLongDate(iso: string, locale = currentLocale): string {
    const { date, timeZone } = dateParts(iso)
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone }).format(date)
}

/* "outubro de 2026" (mes e ano de um marco, 'AAAA-MM' ou 'AAAA-MM-DD'), com a 1a letra maiuscula. */
export function formatMonthYear(iso: string, locale = currentLocale): string {
    const [y, m] = iso.split('-').map(Number)
    const label = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(y!, (m ?? 1) - 1, 15)))
    return label.charAt(0).toLocaleUpperCase(locale) + label.slice(1)
}

/* "06/10/2026, 14:30" */
export function formatDateTime(iso: string, locale = currentLocale): string {
    return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: TIME_ZONE }).format(new Date(iso))
}

/* "14:30" */
export function formatTime(iso: string, locale = currentLocale): string {
    return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', timeZone: TIME_ZONE }).format(new Date(iso))
}

const dayInBrasilia = (d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)

/* So a hora se for hoje (em Brasilia); data e hora se for outro dia (ex.: fim de um bloqueio). */
export function formatWhen(iso: string, locale = currentLocale, now = new Date()): string {
    return dayInBrasilia(new Date(iso)) === dayInBrasilia(now) ? formatTime(iso, locale) : formatDateTime(iso, locale)
}

export function formatCountdown(totalSeconds: number): string {
    const s = Math.max(0, Math.ceil(totalSeconds))
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

export function formatNumber(value: number, locale = currentLocale): string {
    return new Intl.NumberFormat(locale).format(value)
}

/* Idade em anos completos na data de referencia. */
export function ageFromBirthDate(birthDate: string, now = new Date()): number {
    const b = parseIsoDate(birthDate)
    let age = now.getFullYear() - b.getUTCFullYear()
    const beforeBirthday = now.getMonth() < b.getUTCMonth() || (now.getMonth() === b.getUTCMonth() && now.getDate() < b.getUTCDate())
    if (beforeBirthday) age -= 1
    return Math.max(0, age)
}

/* Data local como 'AAAA-MM-DD' (limites do campo de nascimento). */
export function toIsoDate(date: Date): string {
    const p = (n: number) => String(n).padStart(2, '0')
    return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`
}

export function todayIso(now = new Date()): string {
    return toIsoDate(now)
}

/* 18 anos atras: nascimento mais antigo aceito no cadastro de crianca. */
export function minBirthDateIso(now = new Date()): string {
    return toIsoDate(new Date(now.getFullYear() - 18, now.getMonth(), now.getDate()))
}
